import { GraphInputHelpers, ONE_DAY_MS } from '@/fetching/graphql/GraphInputHelpers';
import { VisitDatetimeField, VisitOutcome } from '@/types/visit';
import { EpisodeStatus, OrderType, Ordering } from 'gql-types';

import {
    DEFAULT_CONTAINER_FILTER_WINDOW_DAYS,
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitsRowToLink,
} from '../definitions';
import {
    EpisodeOfCareDashboardContainer,
    PatientDashboardContainer,
    RootEntity,
    VisitDashboardContainer,
} from '../types';
import {
    HH_DISCHARGE_WITH_PENDING_VISITS_CONTAINER,
    HH_EVAL_CONTAINER,
    HH_INCOMPLETE_CARE_TEAM_CONTAINER,
    HH_OVERDUE_VISITS_CONTAINER,
    HH_PATIENT_NO_SHOWS_CONTAINER,
    HH_PENDING_END_OF_EPISODE_CONTAINER,
    HH_TRANSFERS_CONTAINER,
} from './homeHealth';
import { IDashboardVariationConfig } from './types';

// Pinnacle specific service IDs:
// https://docs.google.com/spreadsheets/d/14LjYsiJHynPc0TG_heeSec-F2JpPGPafoJSn8IB6K_M/edit?gid=1593865658#gid=1593865658
const OASIS_DISCHARGE_SERVICE_IDS = [
    '92481', // OASIS-E1 Discharge (SN)
    '92504', // OASIS-E1 Discharge Non-Billable (SN)
    '92475', // OASIS-E1 Discharge Non-Billable (OT)
    '92474', // OASIS-E1 Discharge Non-Billable (PT)
    '92476', // OASIS-E1 Discharge Non-Billable (ST)
    '92492', // OASIS-E1 Discharge (OT)
    '92487', // OASIS-E1 Discharge (PT)
    '92498', // OASIS-E1 Discharge (ST)
    '99562', // OASIS-E2 Discharge (SN)
    '99585', // OASIS-E2 Discharge Non-Billable (SN)
    '99556', // OASIS-E2 Discharge Non-Billable (OT)
    '99555', // OASIS-E2 Discharge Non-Billable (PT)
    '99557', // OASIS-E2 Discharge Non-Billable (ST)
    '99573', // OASIS-E2 Discharge (OT)
    '99568', // OASIS-E2 Discharge (PT)
    '99579', // OASIS-E2 Discharge (ST)
];

const OASIS_RECERT_SERVICE_IDS = [
    '92500', // OASIS-E1 Recertification (SN)
    '92502', // OASIS-E1 Recertification (OT)
    '92501', // OASIS-E1 Recertification (PT)
    '92503', // OASIS-E1 Recertification (ST)
    '99581', // OASIS-E2 Recertification (SN)
    '99583', // OASIS-E2 Recertification (OT)
    '99582', // OASIS-E2 Recertification (PT)
    '99584', // OASIS-E2 Recertification (ST)
];

// TODO(Jessica): Confirm Re-Evaluation services are the correct equivalent
// of "reassessment" for Pinnacle's 30-day reassessment requirement
const REASSESSMENT_SERVICE_IDS = [
    '28388', // OT Re-Evaluation
    '28300', // OT Re-Evaluation w/Supervisory Visit
    '98113', // OT Re-Eval Lymphedema
    '83723', // PT Re-Evaluation
    '35172', // PT Re-evaluation w/Supervisory Visit
    '98067', // PT Lymphedema Re-Eval
    '31744', // ST Re-Evaluation
];

const SOC_SERVICE_IDS = [
    '92477', // OASIS-E1 Start of Care (SN)
    '92505', // OASIS-E1 Start of Care (OT)
    '92483', // OASIS-E1 Start of Care (PT)
    '92494', // OASIS-E1 Start of Care (ST)
    '99558', // OASIS-E2 Start of Care (SN)
    '99586', // OASIS-E2 Start of Care (OT)
    '99564', // OASIS-E2 Start of Care (PT)
    '99575', // OASIS-E2 Start of Care (ST)
];

const ROC_SERVICE_IDS = [
    '92478', // OASIS-E1 Resumption of Care (SN)
    '92489', // OASIS-E1 Resumption of Care (OT)
    '92484', // OASIS-E1 Resumption of Care (PT)
    '92495', // OASIS-E1 Resumption of Care (ST)
    '99559', // OASIS-E2 Resumption of Care (SN)
    '99570', // OASIS-E2 Resumption of Care (OT)
    '99565', // OASIS-E2 Resumption of Care (PT)
    '99576', // OASIS-E2 Resumption of Care (ST)
];

const EVAL_SERVICE_IDS = [
    '4506', // MSW Evaluation
    '3681', // OT Discharge Eval w/ Discharge Summary
    '73472', // OT Eval Lymphedema
    '28387', // OT Evaluation
    '98113', // OT Re-Eval Lymphedema
    '28388', // OT Re-Evaluation
    '28300', // OT Re-Evaluation w/Supervisory Visit
    '35304', // PT Evaluation
    '98065', // PT LSVT Eval
    '98070', // PT Lymphedema Eval
    '98067', // PT Lymphedema Re-Eval
    '83723', // PT Re-Evaluation
    '35172', // PT Re-evaluation w/Supervisory Visit
    '98074', // PT Vestibular RT Eval
    '31742', // ST Evaluation
    '31744', // ST Re-Evaluation
];

const SOC_CONTAINER: VisitDashboardContainer = {
    key: 'pinnacleSoc',
    label: 'SOCs',
    defaultEnabled: true,
    hoverText: 'Start of Care visits that need scheduling.',
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            services: { externalIds: { value: { inList: SOC_SERVICE_IDS } } },
            needsAssignment: { exact: true },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No SOC visits to schedule.',
        ordering: { fulfillmentWindowEndDate: Ordering.ASC },
    },
};

const ROC_CONTAINER: VisitDashboardContainer = {
    key: 'pinnacleRoc',
    label: 'ROCs',
    defaultEnabled: true,
    hoverText: 'Resumption of Care visits that need scheduling.',
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            services: { externalIds: { value: { inList: ROC_SERVICE_IDS } } },
            needsAssignment: { exact: true },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No ROC visits to schedule.',
        ordering: { fulfillmentWindowEndDate: Ordering.ASC },
    },
};

const COMPLETED_VISITS_NOT_MAPPED_TO_ORDERS_CONTAINER: VisitDashboardContainer = {
    key: 'pinnacleCompletedVisitsNotMappedToOrders',
    label: 'Completed SOC/ROC/Evals Not Mapped to Orders',
    defaultEnabled: true,
    hoverText: 'Completed SOC/ROC/Eval visits with no linked order.',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            outcome: { exact: VisitOutcome.COMPLETED },
            hasOrder: { exact: false },
            services: {
                externalIds: {
                    value: {
                        inList: [...SOC_SERVICE_IDS, ...ROC_SERVICE_IDS, ...EVAL_SERVICE_IDS],
                    },
                },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No completed SOC/ROC/Eval visits unlinked to orders.',
        ordering: { startDatetime: Ordering.ASC },
    },
};

const EVALS_BEFORE_SOCS_CONTAINER: VisitDashboardContainer = {
    key: 'pinnacleEvalsBeforeSocs',
    label: 'Evals Before SOCs',
    defaultEnabled: true,
    hoverText: 'Evals scheduled or completed before the patient’s SOC.',
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            services: { externalIds: { value: { inList: EVAL_SERVICE_IDS } } },
            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            relatedVisitCondition: {
                exists: true,
                samePatient: true,
                filter: {
                    services: { externalIds: { value: { inList: SOC_SERVICE_IDS } } },
                    outcome: { exact: VisitOutcome.NONE },
                    effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
                },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        gtOffsetMinutes: 0,
                    },
                ],
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No Evals scheduled before SOCs.',
        ordering: { startDatetime: Ordering.ASC },
    },
};

// Visits that need to be scheduled and are nearing the end of their fulfillment window
// Not copied from homeHealth because we use a different fulfillment window length
const EXPIRING_FULFILLMENT_WINDOW_CONTAINER: VisitDashboardContainer = {
    key: 'pinnacleExpiringFulfillmentWindow',
    label: 'Expiring Fulfillment Windows',
    defaultEnabled: true,
    hoverText: 'Visits with expiring fulfillment windows.',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            patient: {
                isActive: { exact: true },
            },
            needsAssignment: { exact: true },
            fulfillmentWindowEndDate: {
                lte: GraphInputHelpers.date(new Date(Date.now() + 7 * ONE_DAY_MS)),
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No visits with expiring fulfillment windows.',
        ordering: { fulfillmentWindowEndDate: Ordering.ASC },
    },
};

// Patients with visits that still need a service, clinician, or date
// Not copied from homeHealth because we don't have an end date filter here
const PATIENTS_NEEDING_SCHEDULING_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: 'pinnaclePatientsNeedingScheduling',
    label: 'Patients Needing Scheduling',
    defaultEnabled: true,
    hoverText: 'Patients that need scheduling.',
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            patient: { isActive: { exact: true } },
            hasVisits: {
                needsAssignment: { exact: true },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No patients to schedule.',
        // TODO
        // sorting: "fulfillment window end date, soonest first"
        // this is min(visit.fulfillmentWindowEndDate) across the episode?
        // So we cant use episode startDate ASC as northstar does?
        ordering: { startDate: Ordering.ASC },
    },
};

// Visits assigned to inactive clinicians that are still pending
const INACTIVE_CLINICIAN_VISITS_CONTAINER: VisitDashboardContainer = {
    key: 'pinnacleInactiveClinicianVisits',
    label: 'Inactive Clinicians',
    defaultEnabled: true,
    hoverText: 'Visits assigned to inactive clinicians.',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            outcome: { exact: VisitOutcome.NONE },
            assignedClinicians: { isActive: { exact: false } },
            effectiveDate: {
                gte: GraphInputHelpers.date(
                    new Date(Date.now() - DEFAULT_CONTAINER_FILTER_WINDOW_DAYS * ONE_DAY_MS),
                ),
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No visits assigned to inactive clinicians.',
        ordering: { effectiveDate: Ordering.ASC },
    },
};

// Visits tied to a frequency order that has been discontinued
const DISCONTINUED_FREQUENCIES_CONTAINER: VisitDashboardContainer = {
    key: 'pinnacleDiscontinuedFrequencies',
    label: 'Discontinued Frequencies W/ Pending Visits',
    defaultEnabled: true,
    hoverText: 'Visits tied to discontinued frequency orders.',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            patient: { isActive: { exact: true } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: {
                gte: GraphInputHelpers.date(new Date()),
            },
            relatedOrder: {
                isDiscontinued: { exact: true },
                orderType: { exact: OrderType.FREQUENCY },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No visits tied to discontinued frequencies.',
        ordering: { effectiveDate: Ordering.ASC },
    },
};

// Patients with episode status ON_HOLD that need to be monitored and flipped to discharge
const PENDING_PATIENTS_IN_TRANSFER_CONTAINER: PatientDashboardContainer = {
    key: 'pinnaclePendingPatientsInTransfer',
    label: 'Pending Patients Still in Transfer',
    defaultEnabled: true,
    hoverText: 'Patients in transfer status requiring follow-up.',
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: { status: { exact: EpisodeStatus.ON_HOLD } },
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients pending in transfer.',
        // TODO: spec wants sort by duration of transfer status (oldest first).
        // Do we have BE for this?
        ordering: { firstName: Ordering.ASC },
    },
};

// Patients who have an OASIS discharge but still have visits scheduled after its date
const PATIENTS_WITH_VISITS_AFTER_DISCHARGE_CONTAINER: VisitDashboardContainer = {
    key: 'pinnacleVisitsAfterDischarge',
    label: 'Patients with Visits After Discharge',
    defaultEnabled: true,
    hoverText: 'Patients with an OASIS discharge but visits still scheduled after it.',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            services: {
                externalIds: {
                    value: { inList: OASIS_DISCHARGE_SERVICE_IDS },
                },
            },
            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            // There exists a future visit on the same patient scheduled after this discharge
            relatedVisitCondition: {
                exists: true,
                samePatient: true,
                filter: {
                    outcome: { exact: VisitOutcome.NONE },
                    effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
                },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        gtOffsetMinutes: 0,
                    },
                ],
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No patients with visits after discharge.',
        ordering: { effectiveDate: Ordering.ASC },
    },
};

// Episodes with more than one OASIS Discharge or Recert visit that is scheduled or completed
const MULTIPLE_OASIS_PER_EPISODE_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: 'pinnacleMultipleOasisPerEpisode',
    label: 'Multiple OASIS Discharge/Recert per Episode',
    defaultEnabled: true,
    hoverText:
        'Episodes with more than one OASIS Discharge or Recert visit scheduled or completed.',
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            isActiveByDate: { exact: true },
            isActiveField: { exact: true },
            hasVisits: {
                services: { externalIds: { value: { inList: OASIS_DISCHARGE_SERVICE_IDS } } },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                relatedVisitCondition: {
                    exists: true,
                    sameEpisode: true,
                    filter: {
                        services: {
                            externalIds: { value: { inList: OASIS_DISCHARGE_SERVICE_IDS } },
                        },
                        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                    },
                },
            },
            OR: {
                hasVisits: {
                    services: { externalIds: { value: { inList: OASIS_RECERT_SERVICE_IDS } } },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                    relatedVisitCondition: {
                        exists: true,
                        sameEpisode: true,
                        filter: {
                            services: {
                                externalIds: { value: { inList: OASIS_RECERT_SERVICE_IDS } },
                            },
                            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                        },
                    },
                },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No episodes with multiple OASIS Discharge or Recert visits.',
        ordering: { startDate: Ordering.ASC },
    },
};

// Patients with more visits scheduled than their frequency order allows
// eslint-disable-next-line unused-imports/no-unused-vars
const OVERSCHEDULED_PATIENTS_CONTAINER: VisitDashboardContainer = {
    key: 'pinnacleOverscheduledPatients',
    label: 'Overscheduled Patients',
    defaultEnabled: true,
    hoverText: 'Patients with more visits scheduled than their frequency order allows.',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            patient: { isActive: { exact: true } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
            relatedOrder: {
                orderType: { exact: OrderType.FREQUENCY },
                exceedsMaxActiveOccurrences: { exact: true },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No overscheduled patients.',
        ordering: { effectiveDate: Ordering.ASC },
    },
};

// Patients with fewer visits scheduled than their frequency order requires
// eslint-disable-next-line unused-imports/no-unused-vars
const UNDERSCHEDULED_PATIENTS_CONTAINER: VisitDashboardContainer = {
    key: 'pinnacleUnderscheduledPatients',
    label: 'Underscheduled Patients',
    defaultEnabled: true,
    hoverText: 'Patients with fewer visits scheduled than their frequency order requires.',
    heavyQuery: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            patient: { isActive: { exact: true } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
            relatedOrder: {
                orderType: { exact: OrderType.FREQUENCY },
                exceedsMinActiveOccurrences: { exact: true },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No underscheduled patients.',
        ordering: { effectiveDate: Ordering.ASC },
    },
};

const PENDING_REASSESSMENT_CONTAINER: PatientDashboardContainer = {
    key: 'pinnaclePendingReassessment',
    label: 'Pending Reassessment',
    defaultEnabled: true,
    hoverText: 'Patients due for reassessment with none scheduled.',
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasVisits: {
                services: { externalIds: { value: { inList: REASSESSMENT_SERVICE_IDS } } },
            },
            // TODO:
            // "today >= 25 days after max(visits.completedAt where service in
            // REASSESSMENT_SERVICE_IDS)" AND
            // "no upcoming visit in same service list within next 5 days"
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients pending reassessment.',
        // TODO: sort by days since previous reassessment visit (highest first) —
        // requires a derived sort field not currently available.
    },
};

export const PINNACLE_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        HH_INCOMPLETE_CARE_TEAM_CONTAINER,
        SOC_CONTAINER,
        ROC_CONTAINER,
        HH_EVAL_CONTAINER,
        EXPIRING_FULFILLMENT_WINDOW_CONTAINER,
        PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        HH_OVERDUE_VISITS_CONTAINER,
        HH_PATIENT_NO_SHOWS_CONTAINER,
        HH_PENDING_END_OF_EPISODE_CONTAINER,
        PENDING_REASSESSMENT_CONTAINER,
        HH_TRANSFERS_CONTAINER,
        HH_DISCHARGE_WITH_PENDING_VISITS_CONTAINER,
        // TODO: Visits Scheduled After Discipline Discharge. Do we have BE for this?
        INACTIVE_CLINICIAN_VISITS_CONTAINER,
        DISCONTINUED_FREQUENCIES_CONTAINER,
        COMPLETED_VISITS_NOT_MAPPED_TO_ORDERS_CONTAINER,
        EVALS_BEFORE_SOCS_CONTAINER,
        PENDING_PATIENTS_IN_TRANSFER_CONTAINER,
        // OVERSCHEDULED_PATIENTS_CONTAINER,
        // UNDERSCHEDULED_PATIENTS_CONTAINER,
        // Overlapping Episodes. DO we have BE for this?
        MULTIPLE_OASIS_PER_EPISODE_CONTAINER,
        PATIENTS_WITH_VISITS_AFTER_DISCHARGE_CONTAINER,
    ],
};
